export interface ChatMessage {
  id: string;
  sender: 'user' | 'agent';
  text: string;
  timestamp: string;
  fileName?: string;
  isProcessing?: boolean;
}

interface ChatStreamHistoryProps {
  messages: ChatMessage[];
}

export default function ChatStreamHistory({ messages }: ChatStreamHistoryProps) {
  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-10 px-4 rounded-2xl bg-white/60 dark:bg-boxdark/40 border border-dashed border-brand-line dark:border-strokedark mb-6">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#12b98a] to-[#16EA9E] flex items-center justify-center text-brand-navy shadow-sm mb-3.5">
          <svg className="w-7 h-7 fill-current" viewBox="0 0 24 24">
            <path d="M12 2a2 2 0 012 2c0 .74-.4 1.38-1 1.72V7h4a3 3 0 013 3v8a3 3 0 01-3 3H7a3 3 0 01-3-3v-8a3 3 0 013-3h4V5.72A2 2 0 0110 4a2 2 0 012-2zm-4 8a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm8 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm-6 6h4a1 1 0 100-2h-4a1 1 0 100 2z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-brand-navy dark:text-white tracking-tight">
          How can I analyze your invoice today?
        </h2>
        <p className="text-sm text-brand-muted dark:text-bodydark max-w-md mt-1 leading-relaxed">
          Drop your PDF or image below, or choose a smart suggestion to begin real-time extraction and AI audit.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 max-h-[460px] overflow-y-auto mb-6 p-2">
      {messages.map((msg) => (
        <div
          key={msg.id}
          className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`}
        >
          {msg.sender === 'user' ? (
            <div className="bg-brand-vib text-white rounded-2xl rounded-br-sm max-w-[80%] p-4 shadow-sm">
              {msg.fileName && (
                <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/20 text-xs font-mono">
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd" />
                  </svg>
                  <span className="truncate">{msg.fileName}</span>
                </div>
              )}
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.text}</p>
              <div className="text-[10px] font-mono text-white/70 mt-1.5 text-right">
                {msg.timestamp}
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-boxdark text-brand-navy dark:text-white border border-brand-green/30 dark:border-brand-green/20 rounded-2xl rounded-tl-sm max-w-[80%] p-4 shadow-card">
              <div className="flex items-center gap-2 mb-2 pb-2 border-b border-brand-line dark:border-strokedark">
                <span className="w-2 h-2 rounded-full bg-brand-green" />
                <span className="text-[11px] font-mono font-semibold text-brand-green uppercase tracking-wider">
                  AI Extraction Agent
                </span>
              </div>
              {msg.isProcessing ? (
                <div className="flex items-center gap-3 text-sm text-brand-muted dark:text-bodydark py-2">
                  <svg className="w-5 h-5 animate-spin text-brand-vib" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Processing document and running optical analysis…</span>
                </div>
              ) : (
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.text}</p>
              )}
              <div className="text-[10px] font-mono text-brand-soft dark:text-bodydark2 mt-1.5">
                {msg.timestamp}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
